// ProprietaireListParcelles.js
import React, {
    useState,
    useEffect,
    Fragment,
    useMemo,
    useRef,
    useCallback,
  } from 'react';
  import { connect } from "react-redux";
  import { Spinner, Card } from "react-bootstrap";
  import MapGeoJsonService from "../../services/MapGeoJsonService";
  import { columnsConfig } from './ProprietaireListParcellesComponents/columnsConfig';
  import Table from './ProprietaireListParcellesComponents/Table';
  import axios from 'axios';
  import _ from 'lodash';
  import { throttle } from 'lodash';
  
  const ProprietaireListParcelles = (props) => {
    const initialHeight = 240;
    const maxHeight = 560;
    const minHeight = 160;
  
    const mapGeoJsonService = useMemo(() => new MapGeoJsonService(), []);
  
    const [displayMe, setDisplayMe] = useState(true);
    const [title, setTitle] = useState('');
    const [bati, setBati] = useState(true);
    const [height, setHeight] = useState(initialHeight);
  
    // Refs for resizing
    const isResizing = useRef(false);
    const startY = useRef(0);
    const startHeight = useRef(0);
  
    // Array for filtering types that shouldn’t add visual parcelles
    const zoneFilters = ['urba_presc_surf', 'zone_urba'];
  
    // Dynamically compute height based on the number of results
    useEffect(() => {
      if (props.propParcelles) {
        const rowHeight = 40; // estimated height per row
        const computedHeight = Math.min(
          maxHeight,
          Math.max(minHeight, props.propParcelles.length * rowHeight)
        );
        setHeight(computedHeight);
      }
    }, [props.propParcelles, minHeight, maxHeight]);
  
    // Visualisation: add parcels if they exist and propType is not filtered
    useEffect(() => {
      if (props.propParcelles && !zoneFilters.includes(props.propType)) {
        addVisualPropParcelle(props.propParcelles);
      }
    }, [props.propParcelles, props.propType]);
  
    // Visualisation: add geomJson layers
    useEffect(() => {
      if (props.propGeomJson) {
        addVisualPropGeomJson(props.propGeomJson);
      }
    }, [props.propGeomJson]);
  
    // Refactored mapping for propType to title and bati flag
    const propTypeMapping = {
      moral: { title: 'Propriétaire bâti', bati: true },
      moral_nonbati: { title: 'Propriétaire non bâti', bati: false },
      physique_nonbati: { title: 'Propriétaire non bâti', bati: false },
      indivi: { title: 'Propriétaire indivision', bati: false },
      cpte_comm: { title: 'Propriétaire compte communal' },
      prop_pers_morale: { title: 'Propriétaire personne morale' },
      fonc_resid_zu: { title: 'Foncier résiduel zone urbaine (U)' },
      fonc_resid_zu_uf: { title: 'Foncier résiduel unité foncière (U)' },
      fonc_resid_a: { title: 'Foncier résiduel agricole (A)' },
      fonc_resid_a_uf: { title: 'Foncier résiduel unité foncière (A)' },
      non_bati_zu: { title: 'Parcelles non-bâties en zone urbaine' },
      parc_zone_a: { title: 'Parcelles en zone agricole' },
      zone_urba: { title: 'Urbanisme - Zone' },
      urba_presc_surf: { title: 'Urbanisme - Prescriptions (Surf)' },
      permis_pa: { title: "(PA) Permis d'aménager" },
      permis_loc: { title: '(PC) Permis de construire locaux' },
      permis_pc: { title: '(PC) Permis de construire logements' },
      permis_dp: { title: '(DP) Déclarations préalables' },
    };
  
    useEffect(() => {
      const config = propTypeMapping[props.propType];
      if (config) {
        setTitle(config.title);
        if ('bati' in config) {
          setBati(config.bati);
        }
      } else {
        setTitle('Propriétaire bâti');
        setBati(true);
      }
    }, [props.propType]);
  
    useEffect(() => {
      if (!props.displayPropList) {
        mapGeoJsonService.clearPropParcelleLayers();
      }
    }, [props.displayPropList, mapGeoJsonService]);
  
    const columns = useMemo(() => columnsConfig[props.propType] || [], [props.propType]);
  
    // API call to add visual prop parcelles
    const addVisualPropParcelle = (items) => {
      const idParcelles = _.uniq(_.map(items, 'parcelle'));
      const url = "/Api/geodata/vmap/prop/parcelle/geom";
      const params = {
        "codes_com": idParcelles.toString()
      };
  
      if (idParcelles.length > 0) {
        axios.post(url, params)
          .then(result => {
            if (result.data && result.data.length > 0) {
              mapGeoJsonService.addPropParcellesLayer(props.refMap, result.data);
            }
          })
          .catch(err => {
            console.error(err);
          });
      }
    };
  
    // Add geomJson layers
    const addVisualPropGeomJson = (items) => {
      items.forEach((element) => mapGeoJsonService.addZoneLayer(element.geom_json));
    };
  
    const centerOnParcelle = (item) => {
      mapGeoJsonService.centerOnGeom(item.geom_json);
    };
  
    const handleClosePanel = () => {
      props.setDisplayPropList(false);
      props.cleanVectorLayer(false);
      mapGeoJsonService.clearZoneLayer();
      props.setShowSearchPanel(false);
    };
  
    // Throttle mouse move events
    const throttledMouseMove = useCallback(
      throttle((e) => {
        if (!isResizing.current) return;
        const deltaY = startY.current - e.clientY;
        const newHeight = startHeight.current + deltaY;
        if (newHeight > minHeight && newHeight < maxHeight) {
          setHeight(newHeight);
        }
      }, 50),
      [minHeight, maxHeight]
    );

    const throttledTouchMove = useCallback(
      throttle((e) => {
        if (!isResizing.current) return;
        const deltaY = startY.current - e.touches[0].clientY;
        const newHeight = startHeight.current + deltaY;
        if (newHeight > minHeight && newHeight < maxHeight) {
          setHeight(newHeight);
        }
      }, 50),
      [minHeight, maxHeight]
    );

    const handleMouseMove = useCallback((e) => {
      if (!isResizing.current) return;
      const deltaY = startY.current - e.clientY;
      const newHeight = startHeight.current + deltaY;
      if (newHeight > minHeight && newHeight < maxHeight) {
        setHeight(newHeight);
      }
    }, [minHeight, maxHeight]);

    const handleMouseUp = useCallback(() => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = 'default';
        document.removeEventListener('mousemove', throttledMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }
    }, [throttledMouseMove]);

    const onMouseDown = useCallback((e) => {
      isResizing.current = true;
      startY.current = e.clientY;
      startHeight.current = height;
      document.body.style.cursor = 'ns-resize';
      document.addEventListener('mousemove', throttledMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      e.preventDefault(); // Prevents text selection
    }, [height, throttledMouseMove, handleMouseUp]);
    
    const handleTouchMove = useCallback((e) => {
      if (!isResizing.current) return;
      const deltaY = startY.current - e.touches[0].clientY; // Access touch event
      const newHeight = startHeight.current + deltaY;
      if (newHeight > minHeight && newHeight < maxHeight) {
        setHeight(newHeight);
      }
    }, [minHeight, maxHeight]);
    
    const handleTouchEnd = useCallback(() => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = 'default';
        document.removeEventListener('touchmove', throttledTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      }
    }, [throttledTouchMove]);

    const onTouchStart = useCallback((e) => {
      isResizing.current = true;
      startY.current = e.touches[0].clientY; // Use first touch
      startHeight.current = height;
      document.addEventListener('touchmove', throttledTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      e.preventDefault(); // Prevents unwanted scrolling
    }, [height, throttledTouchMove, handleTouchEnd]);

    const handleMinimizeTable = () => {
      setDisplayMe(false);
      setHeight(75);
    };
  
    const handleMaximizeTable = () => {
      setDisplayMe(true);
      setHeight(initialHeight);
    };
  
    // Cleanup on unmount and cancel pending throttled calls
    useEffect(() => {
      return () => {
        document.body.style.cursor = 'default';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        throttledMouseMove.cancel && throttledMouseMove.cancel();
      };
    }, [handleMouseMove, handleMouseUp, throttledMouseMove]);
  
    return (
      <Fragment>
        {props.displayPropList && (
          <div
            id="float_prop_parcelle"
            className="card ueThemeBackground"
            style={{
              width: "100%",
              height: `${height}px`,
              fontSize: "0.60rem",
              marginLeft: "auto",
              marginRight: "auto",
              zIndex: "100",
              position: "absolute",
              bottom: "0px",
              display: 'flex',
              flexDirection: 'column',
              transition: 'height 0.2s ease',
            }}
          >
            {displayMe && (
              <Fragment>
                {/* Resizer Handle */}
                <div
                  onMouseDown={onMouseDown}
                  onTouchStart={onTouchStart}
                  style={{
                    height: '15px',
                    cursor: 'ns-resize',
                    width: '100%',
                  }}
                  className="d-flex justify-content-center align-items-center text-light fa-xl mt-2"
                >
                  <i className="fas fa-grip-horizontal"></i>
                </div>
                <div className="d-flex justify-content-between mb-2 px-4">
                  <div>
                    <h5 className="ueColorBase d-inline-flex mr-3">{title}</h5>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleMinimizeTable}
                    >
                      <i className="fas fa-caret-right"></i> Replier
                    </button>
                  </div>
                  <div className="p-1 pb-0 mb-1 align-self-center text-primary ue-font-weight-7 fa-lg">
                    {props.propParcelles.length} enregistrement(s) retourné(s)
                  </div>
                  <button
                    className="btn btn-primary btn-sm mb-1"
                    onClick={handleClosePanel}
                  >
                    Fermer
                  </button>
                </div>
              </Fragment>
            )}
            <div className="card-body ueColorBase pt-0" style={{ flex: 1, overflow: 'auto' }}>
              {displayMe ? (
                <Fragment>
                  <div style={{ overflow: "auto" }}>
                    <Table
                      columns={columns}
                      data={props.propParcelles}
                      onCenter={centerOnParcelle}
                    />
                  </div>
                </Fragment>
              ) : (
                <div className="d-flex justify-content-between mb-1 mt-4">
                  <h5 className="ueColorBase d-inline-flex mr-3">{title}</h5>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleMaximizeTable}
                  >
                    <i className="fas fa-caret-down"></i> Afficher
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Fragment>
    );
  };
  
  const mapStateToProps = state => ({
    displayPropList: state.SearchReducer.displayPropList,
    propParcelles: state.SearchReducer.propParcelles,
    propGeomJson: state.SearchReducer.propGeomJson,
    propType: state.SearchReducer.propType,
    refMap: state.MapReducer.refMap,
  });
  
  const mapDispatchToProps = dispatch => ({
    setDisplayPropList: (display) =>
      dispatch({ type: 'DISPLAY_PROP_LIST_PARCELLES', data: { displayPropList: display } }),
    cleanVectorLayer: (clear) =>
      dispatch({ type: 'CLEAR_VECTOR_LAYER', data: clear }),
    setShowSearchPanel: (show) =>
      dispatch({ type: 'SHOW_SEARCH_PANEL', data: show }),
  });
  
  export default connect(mapStateToProps, mapDispatchToProps)(ProprietaireListParcelles);
  